import {
  AHQueryHandlerErrorFallbackStatic,
  AHQueryHandlerLoading,
  AHText,
  Box,
  ScaleFactorProvider,
  useScaleFactor,
} from "arkh";
import { StyleSheet } from "react-native";

import React, {
  useMemo,
  useRef,
  Suspense,
  useEffect,
  useCallback,
  useTransition,
} from "react";

import { useFragment, graphql } from "react-relay";

import { TodoFocusScreen_widgetTodoOverview$key } from "./__generated__/TodoFocusScreen_widgetTodoOverview.graphql";
import { TodoFocusScreenSrolledSubscription } from "./__generated__/TodoFocusScreenSrolledSubscription.graphql";
import { useSubscriptionWithSkip } from "widgets/src/utils/useSubscriptionWithSkip";
import { TodoFocusScreenListHeader_widgetTodoOverview$key } from "./__generated__/TodoFocusScreenListHeader_widgetTodoOverview.graphql";
import { TodoDetail } from "./TodoDetail";
import {
  WidgetTodoOverviewContentMedium,
  WidgetTodoOverviewListRef,
} from "widgets/src/WidgetTodoOverview/WidgetTodoOverviewContentMedium";
import { WidgetTodoOverviewContentMediumGrouped } from "widgets/src/WidgetTodoOverview/WidgetTodoOverviewContentMediumGrouped";
import { ErrorBoundary } from "react-error-boundary";

const noOp = () => {};

export const TodoFocusScreen = (props: {
  widget: TodoFocusScreen_widgetTodoOverview$key;
  todoId?: string;
  storecastId: string;
  focusItem: (id: string) => void;
  isTouchMode: boolean;
}) => {
  const widget = useFragment(
    graphql`
      fragment TodoFocusScreen_widgetTodoOverview on WidgetTodoOverview {
        id
        ...TodoFocusScreenListHeader_widgetTodoOverview
        ...WidgetTodoOverviewContentMediumPaginated
        todoContent: content {
          ... on WidgetTodoOverviewContentMediumGrouped {
            __typename
            ...WidgetTodoOverviewContentMediumGrouped_WidgetTodoOverviewContentMediumGrouped
            subjectGroups {
              todos {
                id
                ...TodoDetailContent_todo
              }
            }
          }
          ... on WidgetTodoOverviewContentMedium {
            __typename
          }
        }
      }
    `,
    props.widget
  );

  const sf = useScaleFactor();
  const flRef = useRef<WidgetTodoOverviewListRef>(null);

  const [isPending, startTransition] = useTransition();
  const [delayedIssueId, setDelayedIssueId] = React.useState<
    string | undefined
  >(props.todoId);

  useSubscriptionWithSkip<TodoFocusScreenSrolledSubscription>(
    useMemo(
      () => ({
        subscription: graphql`
          subscription TodoFocusScreenSrolledSubscription($widgetId: ID!) {
            widgetFocusScrollChanged(widgetId: $widgetId)
          }
        `,
        variables: { widgetId: widget.id },
        onNext: (data) => {
          flRef.current?.scrollToItem(data?.widgetFocusScrollChanged ?? null);
        },
      }),
      [widget.id]
    )
  );

  const { focusItem } = props;
  const onSelectDetailItem = useCallback(
    (id: string) => {
      startTransition(() => {
        setDelayedIssueId(id);
      });
      focusItem(id);
    },
    [focusItem]
  );
  return (
    <Box
      space="xlarge"
      style={{
        flex: 1,
        width: "100%",
        alignSelf: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        style={{
          flex: 1,
          flexDirection: "row",
          overflow: "hidden",
          pointerEvents: "auto",
        }}
        cornerRadius="medium"
        color="surface"
      >
        <Box
          style={{
            flexBasis: "20%",
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Suspense fallback={null}>
            <ListHeader widget={widget} />
          </Suspense>
          {widget.todoContent?.__typename ===
          "WidgetTodoOverviewContentMedium" ? (
            <WidgetTodoOverviewContentMedium
              ref={flRef}
              widget={widget}
              isFocused
              shouldDimInactive={false}
              hideShowMoreButton
              onPressItem={onSelectDetailItem}
              setScroll={noOp}
              interactive
              highlightedItemId={delayedIssueId}
            />
          ) : widget.todoContent?.__typename ===
            "WidgetTodoOverviewContentMediumGrouped" ? (
            <WidgetTodoOverviewContentMediumGrouped
              ref={flRef}
              onPressItem={onSelectDetailItem}
              highlightedItemId={delayedIssueId}
              shouldDimInactive={false}
              interactive
              setScroll={noOp}
              isFocused
              content={widget.todoContent}
            />
          ) : null}
        </Box>
        {props.todoId ? (
          <Box
            style={{
              flexBasis: "80%",
              borderLeftWidth: StyleSheet.hairlineWidth,
            }}
            borderColor="surfaceBorder"
          >
            <ScaleFactorProvider value={sf * 1.2}>
              <ErrorBoundary
                FallbackComponent={AHQueryHandlerErrorFallbackStatic}
              >
                <Suspense fallback={<AHQueryHandlerLoading />}>
                  <TodoDetail
                    todoId={props.todoId}
                    isTouchMode={props.isTouchMode}
                  />
                </Suspense>
              </ErrorBoundary>
            </ScaleFactorProvider>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

function ListHeader(props: {
  widget: TodoFocusScreenListHeader_widgetTodoOverview$key;
}) {
  const widget = useFragment(
    graphql`
      fragment TodoFocusScreenListHeader_widgetTodoOverview on WidgetTodoOverview {
        category {
          title
        }
        teams {
          name
        }
      }
    `,
    props.widget
  );

  return (
    <Box space="medium">
      <AHText type="title">{widget.category?.title}</AHText>
      <AHText type="detail">
        {widget.teams.map(({ name }) => name).join(", ")}
      </AHText>
    </Box>
  );
}
